"use client";

import { useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import {
  FaLeaf,
  FaHandshake,
  FaTractor,
  FaStore,
  FaGavel,
  FaUserShield,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";

interface FeatureCard {
  icon: React.ReactNode;
  title: string;
  desc: string;
}

const featureCards: FeatureCard[] = [
  {
    icon: <FaLeaf className="text-2xl text-green-800" />,
    title: "Direct Access to Agri Inputs",
    desc: "Fertilizers, seeds, farm machinery — all just a click away.",
  },
  {
    icon: <FaStore className="text-2xl text-green-800" />,
    title: "Sell Produce Directly",
    desc: "Fruits, vegetables, grains — farmers connect straight to buyers.",
  },
  {
    icon: <FaHandshake className="text-2xl text-green-800" />,
    title: "Marketplace for Vendors & Buyers",
    desc: "A trusted space for transactions that support local agri-business.",
  },
  {
    icon: <FaTractor className="text-2xl text-green-800" />,
    title: "Productivity & Profitability Services",
    desc: "Tools and services designed to improve every farmer's bottom line.",
  },
];

const registrationItems: string[] = [
  "Vendors must register with valid personal and business info (ID proof, business name, GST number) and subscribe to list offerings.",
  "Buyers can browse listings freely, but must register to post inquiries and contact vendors directly.",
];

const platformUsageItems: string[] = [
  "Use the platform ethically, respectfully, and with transparency.",
  "Vendors should upload clear, honest listings with pricing and contact details.",
  "Buyers must communicate with vendors in a professional and courteous manner.",
];

const liabilityItems: string[] = [
  "Failed or fraudulent transactions",
  "Miscommunication between users",
  "Non-fulfillment of orders or services",
  "Product or service quality issues",
];

interface BulletItemProps {
  children: React.ReactNode;
}

const BulletItem = ({ children }: BulletItemProps) => (
  <li className="flex items-start gap-3">
    <span className="mt-1.5 w-2 h-2 rounded-sm bg-green-800 flex-shrink-0" />
    <span className="text-gray-800 text-sm sm:text-base font-medium leading-relaxed">{children}</span>
  </li>
);

function UserGuidelinesContent() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          const yOffset = -99;
          const y =
            el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }
  }, [searchParams]);

  return (
    <div className="bg-white min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-12">
        
        {/* Header Block */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-green-50 text-green-800 mb-2">
            <FaUserShield className="text-3xl" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-green-900 tracking-tight">
            User Guidelines &amp; Terms
          </h1>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto font-medium">
            Understand how our platform works, our code of conduct, registration guidelines, and key liability rules.
          </p>
        </div>

        {/* Section 1: Who We Are / Why Choose Karnataka Agribusiness */}
        <div id="WWA" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-xl sm:text-2xl font-bold text-green-800">
              Why Choose Karnataka Agribusiness?
            </h2>
            <p className="text-gray-800 text-sm sm:text-base font-medium max-w-3xl mx-auto leading-relaxed">
              <strong className="text-green-805">Karnataka Agribusiness</strong> is a reliable online platform that connects farmers, vendors, buyers, and service providers across Karnataka and nearby regions. We simplify the process of buying and selling agricultural products and services - all while boosting productivity and profitability for the farming community.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-4 border-t border-gray-100">
            {featureCards.map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 bg-white border border-gray-200 p-5 rounded-sm shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md group"
              >
                <div className="p-3 bg-green-50 rounded-sm text-green-800 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 2: User Guidelines */}
        <div id="UG" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-8">
          <h2 className="text-2xl font-bold text-green-800 border-b border-gray-150 pb-3">
            Platform User Guidelines
          </h2>

          {/* Registration & Access */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold flex items-center gap-2 text-green-800">
              <FaUserShield /> <span>Registration &amp; Access</span>
            </h3>
            <ul className="space-y-2.5 ml-1">
              {registrationItems.map((text, idx) => (
                <BulletItem key={idx}>{text}</BulletItem>
              ))}
            </ul>
          </div>

          {/* Platform Usage */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold flex items-center gap-2 text-green-800">
              <FaGavel /> <span>Platform Usage</span>
            </h3>
            <ul className="space-y-2.5 ml-1">
              {platformUsageItems.map((text, idx) => (
                <BulletItem key={idx}>{text}</BulletItem>
              ))}
            </ul>
          </div>
        </div>

        {/* Section 3: Disclaimer */}
        <div id="DSLM" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-6">
          <h2 className="text-xl sm:text-2xl font-bold text-red-800 border-b border-gray-155 pb-3 flex items-center gap-2">
            <FaExclamationTriangle className="text-red-700" /> <span>Disclaimer</span>
          </h2>

          <div className="space-y-4 text-gray-800 text-sm sm:text-base font-medium leading-relaxed">
            <p>
              <strong className="text-green-800 font-bold">Platform Role:</strong> Karnataka Agribusiness is solely a facilitator connecting buyers, sellers, and service providers in the agri space. We do <u>not</u> guarantee quality, quantity, or outcomes of transactions made through the platform.
            </p>
            <p>
              <strong className="text-green-800 font-bold">No Guarantee on Leads:</strong> Lead volume and effectiveness depend on your product, pricing, responsiveness, and demand — not just platform visibility. We promote, but outcomes vary.
            </p>
            <p>
              <strong className="text-green-800 font-bold">Platform Maintenance Fee:</strong> Fees go toward tech support, promotions, and infra maintenance. They&apos;re not a performance guarantee.
            </p>
            <p>
              <strong className="text-green-800 font-bold">User Responsibility:</strong> Users must verify authenticity and quality before any transaction. We don&apos;t vet listings or involve ourselves in deals.
            </p>
            <div>
              <strong className="text-green-800 font-bold">Liability Limitations:</strong>
              <ul className="list-none ml-4 mt-2 space-y-2">
                {liabilityItems.map((item, idx) => (
                  <BulletItem key={idx}>{item}</BulletItem>
                ))}
              </ul>
              <p className="mt-2 text-gray-800">
                Karnataka Agribusiness holds no liability for losses or disputes that may arise.
              </p>
            </div>
            <p>
              <strong className="text-green-800 font-bold">Legal Jurisdiction:</strong> Any disputes will be settled under the jurisdiction of courts in Bengaluru, Karnataka.
            </p>
            <p className="text-sm italic text-gray-500 pt-2 border-t border-gray-100 font-medium">
              We advise all users to exercise caution, perform due diligence, and make informed decisions.
            </p>
          </div>
        </div>

        {/* Section 4: Contact */}
        <div id="add" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-6">
          <h2 className="text-xl sm:text-2xl font-bold text-green-800 text-center">
            Contact Information
          </h2>
          <p className="text-sm font-medium text-gray-600 text-center max-w-2xl mx-auto">
            For questions, concerns, or data inquiries regarding these Guidelines, please reach out to us:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-50 rounded-sm text-green-800 animate-pulse">
                <FaEnvelope className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Email Us</p>
                <a
                  href="mailto:karnatakaagribusiness@gmail.com"
                  className="text-sm font-semibold text-green-800 hover:underline break-all"
                >
                  karnatakaagribusiness@gmail.com
                </a>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-50 rounded-sm text-green-800">
                <FaMapMarkerAlt className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Our Address</p>
                <p className="text-sm font-semibold text-gray-900">
                  Way2Agribusiness India Pvt Ltd, Bengaluru, Karnataka, India.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-50 rounded-sm text-green-800">
                <FaPhone className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Call Us</p>
                <p className="text-sm font-semibold text-gray-900">
                  9449004956 / 8277078435
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function UserGuidelinesPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-500 font-medium text-sm">Loading User Guidelines...</div>}>
      <UserGuidelinesContent />
    </Suspense>
  );
}